/*
  The demo layer's only door to browser storage (architecture, "השכבה המדומה").
  No other file in the theme reads or writes localStorage or sessionStorage.
  Stage 1 holds the last world, the wishlist ids and the newsletter addresses
  registered from this browser; stage 2 adds the grid view switch; stage א4
  adds signing in as Noa, her profile and details, fitting requests and
  return requests; stage א5 adds the AMOR BY YOU answers, the Your Private
  Edit versions, the saved Edits and the Build My Look directions; stage
  א6 adds the order flags of the return and cancellation forms and the
  Find My Piece requests; stage א7 adds the color Noa's wishlist items were
  saved in, and the last gift note conversation (update 3); stage א12 adds
  the "סמוך עלי" conversations and their order numbers. Her
  opening values come from the demo_customer metaobject
  (snippets/demo-customer.liquid) and are seeded on the first sign-in. The
  address ?reset=1 clears everything.
  Loaded in <head> without defer, so the saved grid view and the signed-in
  state are on <html> before the page draws (no jump).
*/
(() => {
  const KEY = 'amor-demo-v1';
  const WORLDS = ['women', 'men'];
  const VIEWS = ['default', 'alt'];
  const RECENT_LIMIT = 8;
  const ANSWERS_KEPT = 12;
  const EDITS_KEPT = 8;
  const BUILT_LOOKS_KEPT = 6;
  const TRUST_CHATS_KEPT = 6;
  const DAY_MS = 24 * 60 * 60 * 1000;

  const read = () => {
    try {
      return JSON.parse(window.localStorage.getItem(KEY)) || {};
    } catch {
      return {};
    }
  };

  const write = (state) => {
    try {
      window.localStorage.setItem(KEY, JSON.stringify(state));
    } catch {
      /* private mode or blocked storage: the demo still works for this visit */
    }
  };

  let state = read();
  // Stage א12 first kept one "סמוך עלי" conversation; now there is one per place (trustChats).
  if (state.trustChat) {
    delete state.trustChat;
    write(state);
  }

  if (new URLSearchParams(window.location.search).has('reset')) {
    state = {};
    try {
      window.localStorage.removeItem(KEY);
    } catch {
      /* nothing stored */
    }
    // The tab's small copies of request images too: the ids start again at A-1 and E-1 (stage א7).
    try {
      Object.keys(window.sessionStorage)
        .filter((name) => name.startsWith(`${KEY}-`))
        .forEach((name) => window.sessionStorage.removeItem(name));
    } catch {
      /* nothing stored */
    }
  }

  // The phone header shows its heart only while the list has items (update 2, chapter 2).
  const markWishlist = () => document.documentElement.toggleAttribute('data-has-wishlist', list('wishlist').length > 0);
  // Everything personal is in the page already, hidden for guests by CSS (architecture, stage א4).
  const markSignedIn = () => document.documentElement.toggleAttribute('data-signed-in', state.signedIn === true);

  const set = (key, value) => {
    state = { ...state, [key]: value };
    write(state);
    if (key === 'wishlist') markWishlist();
    if (key === 'signedIn') markSignedIn();
    document.dispatchEvent(new CustomEvent('demo-store:change', { detail: { key, value } }));
  };

  const list = (key) => (Array.isArray(state[key]) ? [...state[key]] : []);
  const normalizeEmail = (email) => String(email).trim().toLowerCase();
  const gridView = () => (VIEWS.includes(state.gridView) ? state.gridView : 'default');
  const clone = (value) => (value == null ? value : JSON.parse(JSON.stringify(value)));

  /** Today at midnight plus a number of days, in the visitor's time zone. */
  const dateFromDays = (days) => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() + Number(days || 0));
    return date;
  };

  /** YYYY-MM-DD in the visitor's time zone. */
  const isoDate = (date) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

  const parseDate = (iso) => {
    const [year, month, day] = String(iso).split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  /** Ids are counters, not random, so the same steps give the same result. */
  const nextId = (prefix) => {
    const counters = { ...(state.counters || {}) };
    counters[prefix] = (counters[prefix] || 0) + 1;
    set('counters', counters);
    return `${prefix}-${counters[prefix]}`;
  };

  // A seeded request keeps its date relative to today (spec chapter 22: dates never go stale).
  const materialize = (request) => (request.days == null ? request : { ...request, date: isoDate(dateFromDays(request.days)) });

  document.documentElement.dataset.gridView = gridView();
  markWishlist();
  markSignedIn();

  window.DemoStore = {
    /** Category grid: default is 2 per row on the phone and 3 on desktop; alt is 1 and 4. */
    gridView,

    setGridView(view) {
      if (!VIEWS.includes(view)) return;
      document.documentElement.dataset.gridView = view;
      if (view !== state.gridView) set('gridView', view);
    },

    lastWorld() {
      return WORLDS.includes(state.lastWorld) ? state.lastWorld : null;
    },

    setLastWorld(world) {
      if (WORLDS.includes(world) && world !== state.lastWorld) set('lastWorld', world);
    },

    wishlist() {
      return list('wishlist');
    },

    inWishlist(productId) {
      return list('wishlist').includes(String(productId));
    },

    /**
     * Adds or removes a product; returns true when it is now in the list. A color
     * chosen on the card (stage א7, note 18) is saved with it, with its image.
     */
    toggleWishlist(productId, color, image) {
      const id = String(productId);
      const ids = list('wishlist');
      const index = ids.indexOf(id);
      if (index === -1) {
        ids.push(id);
        if (color) set('wishlistColors', { ...(state.wishlistColors || {}), [id]: { color, image: image || '' } });
      } else {
        ids.splice(index, 1);
        // Saved again later, it is saved without a color.
        if (state.wishlistColors?.[id]) {
          const colors = { ...state.wishlistColors };
          delete colors[id];
          set('wishlistColors', colors);
        }
      }
      set('wishlist', ids);
      return index === -1;
    },

    /** The color a listed item was saved in, with its image in that color: { color, image }, or null. */
    wishlistColor(productId) {
      return state.wishlistColors?.[String(productId)] || null;
    },

    newsletterHas(email) {
      return list('newsletter').includes(normalizeEmail(email));
    },

    newsletterAdd(email) {
      const address = normalizeEmail(email);
      const addresses = list('newsletter');
      if (!addresses.includes(address)) set('newsletter', [...addresses, address]);
    },

    /** Product handles, most recent first (product page, search). */
    recentlyViewed() {
      return list('recentlyViewed');
    },

    addRecentlyViewed(handle) {
      const handles = list('recentlyViewed').filter((item) => item !== handle);
      set('recentlyViewed', [handle, ...handles].slice(0, RECENT_LIMIT));
    },

    /** "Notify me when it's back" requests: { handle, variantId, contact }. Stored only here. */
    notifyRequests() {
      return list('notifyRequests');
    },

    addNotifyRequest(request) {
      const requests = list('notifyRequests').filter(
        (item) => !(item.handle === request.handle && item.variantId === request.variantId)
      );
      set('notifyRequests', [...requests, { ...request, at: new Date().toISOString().slice(0, 10) }]);
    },

    hasNotifyRequest(handle, variantId) {
      return list('notifyRequests').some((item) => item.handle === handle && item.variantId === variantId);
    },

    /**
     * An image attached to a request crosses one page load (the field sends to
     * the AMOR BY YOU page) in this tab's session storage, and is removed as
     * soon as that page reads it. It is never kept (spec chapter 27).
     */
    setRequestImage(dataUrl) {
      try {
        window.sessionStorage.setItem(`${KEY}-image`, dataUrl);
      } catch {
        /* too large or blocked: the request goes without the image */
      }
    },

    takeRequestImage() {
      try {
        const dataUrl = window.sessionStorage.getItem(`${KEY}-image`);
        window.sessionStorage.removeItem(`${KEY}-image`);
        return dataUrl;
      } catch {
        return null;
      }
    },

    /* --- Signing in (spec chapter 21): every number and code signs in as Noa ----- */

    signedIn() {
      return state.signedIn === true;
    },

    /**
     * Signs in. `seed` is Noa's data from the demo_customer metaobject. What she
     * can change in the browser is seeded only once, so her edits survive
     * signing out and in again; the reset in "taste and sizes" brings back the seed.
     */
    signIn(seed) {
      const data = seed || {};
      if (!state.details) {
        set('details', {
          name: data.name || '',
          phone: data.phone || '',
          email: data.email || '',
          birthday: data.birthday || '',
          address: data.address || '',
          marketing: false,
        });
      }
      if (!state.profileSeed && data.profile) set('profileSeed', clone(data.profile));
      if (!state.profile && data.profile) set('profile', clone(data.profile));
      if (data.appointment && !list('appointments').some((item) => item.id === 'noa')) {
        set('appointments', [{ ...clone(data.appointment), id: 'noa' }, ...list('appointments')]);
      }
      const wishlist = list('wishlist');
      const additions = (data.wishlist || []).map(String).filter((id) => !wishlist.includes(id));
      // Her items keep the color she saved them in (the clutch in gold); one the visitor listed already stays as it is.
      const colors = { ...(state.wishlistColors || {}) };
      additions.forEach((id) => {
        if (data.wishlistColors?.[id]) colors[id] = clone(data.wishlistColors[id]);
      });
      if (additions.some((id) => colors[id])) set('wishlistColors', colors);
      if (additions.length) set('wishlist', [...wishlist, ...additions]);
      // The gift note conversation starts again, addressed to her (update 3, chapter 3); "סמוך עלי" too (stage א12).
      set('greetingChat', null);
      set('trustChats', null);
      set('signedIn', true);
    },

    /** Back to guest. The cart and the wishlist stay (spec chapter 21); the conversations start again. */
    signOut() {
      set('greetingChat', null);
      set('trustChats', null);
      set('signedIn', false);
    },

    /** Name, phone, email, birthday, address and the marketing choice. */
    details() {
      return { ...(state.details || {}) };
    },

    setDetails(details) {
      set('details', { ...(state.details || {}), ...details });
    },

    /** { brands, sizes: [{ system, size, label }], colors, styles }, or null before the first sign-in. */
    profile() {
      return clone(state.profile) || null;
    },

    setProfile(profile) {
      set('profile', clone(profile));
    },

    resetProfile() {
      if (state.profileSeed) set('profile', clone(state.profileSeed));
    },

    /** Noa's usual size in a size system (IT, FR, EU), from her editable profile. */
    sizeFor(system) {
      const entry = (state.profile?.sizes || []).find((item) => item.system === system);
      return entry ? entry.size : null;
    },

    eventRsvp() {
      return state.eventRsvp === true;
    },

    setEventRsvp(value) {
      set('eventRsvp', value === true);
    },

    /* --- Fitting requests (spec chapter 31) ------------------------------------ */

    /** Every request, each with an ISO `date`. Statuses: pending, approved, cancelled. */
    appointments() {
      return list('appointments').map(materialize);
    },

    /** Saves a new request and returns it with its id. */
    addAppointment(request) {
      const saved = { ...clone(request), id: nextId('F'), status: 'pending', createdAt: isoDate(new Date()) };
      set('appointments', [...list('appointments'), saved]);
      return saved;
    },

    /** A new date drops the relative seed date and goes back to pending. */
    updateAppointment(id, patch) {
      let updated = null;
      const requests = list('appointments').map((item) => {
        if (item.id !== id) return item;
        updated = { ...item, ...clone(patch) };
        if (patch.date) delete updated.days;
        return updated;
      });
      set('appointments', requests);
      return updated && materialize(updated);
    },

    /** Steps and choices of an unfinished request, kept when the drawer closes. */
    fittingDraft() {
      return clone(state.fittingDraft) || null;
    },

    setFittingDraft(draft) {
      set('fittingDraft', draft ? clone(draft) : null);
    },

    /* --- Return requests with a courier (update 2, chapter 8) ------------------ */

    returns() {
      return list('returns');
    },

    addReturn(request) {
      const saved = { ...clone(request), id: nextId('R'), status: 'awaiting', createdAt: isoDate(new Date()) };
      set('returns', [...list('returns'), saved]);
      return saved;
    },

    /* --- Forms (stage א6): the order flags, and Find My Piece requests --------- */

    /**
     * A sent return or cancellation form ({ type: 'return' | 'cancel', order }),
     * so the order in the account area shows it (spec chapter 35). The form
     * itself went to the store's mailbox; only the flag is kept here.
     */
    formRequests() {
      return list('formRequests');
    },

    addFormRequest(request) {
      const saved = { ...clone(request), id: nextId('Q'), createdAt: isoDate(new Date()) };
      set('formRequests', [...list('formRequests'), saved]);
      return saved;
    },

    /** Find My Piece (spec chapter 32), mock: what she asked for, kept only here. */
    findRequests() {
      return list('findRequests');
    },

    addFindRequest(request) {
      const saved = { ...clone(request), id: nextId('P'), createdAt: isoDate(new Date()) };
      set('findRequests', [...list('findRequests'), saved]);
      return saved;
    },

    /* --- AMOR BY YOU (stage א5): answers, Your Private Edit, saved Edits -------- */

    /**
     * The server's answers, kept so a refresh or going back does not ask the
     * model again (spec chapter 27). Only the last few are kept.
     */
    answer(id) {
      return clone(list('answers').find((item) => item.id === id)) || null;
    },

    addAnswer(answer) {
      const saved = { ...clone(answer), id: nextId('A') };
      set('answers', [...list('answers'), saved].slice(-ANSWERS_KEPT));
      return saved;
    },

    updateAnswer(id, patch) {
      set('answers', list('answers').map((item) => (item.id === id ? { ...item, ...clone(patch) } : item)));
    },

    /**
     * A Your Private Edit: the request and every version of the result, oldest
     * first; `current` is the one shown ("back to the previous version").
     */
    edit(id) {
      return clone(list('edits').find((item) => item.id === id)) || null;
    },

    addEdit(edit) {
      const saved = { ...clone(edit), id: nextId('E'), createdAt: isoDate(new Date()) };
      set('edits', [...list('edits'), saved].slice(-EDITS_KEPT));
      return saved;
    },

    updateEdit(id, patch) {
      let updated = null;
      set(
        'edits',
        list('edits').map((item) => {
          if (item.id !== id) return item;
          updated = { ...item, ...clone(patch) };
          return updated;
        })
      );
      return clone(updated);
    },

    /** Edits saved to the account ("Edits שמורים"), oldest first. A saved Edit never changes. */
    savedEdits() {
      return list('savedEdits');
    },

    savedEdit(id) {
      return clone(list('savedEdits').find((item) => item.id === id)) || null;
    },

    addSavedEdit(edit) {
      const saved = { ...clone(edit), id: nextId('S'), savedAt: isoDate(new Date()) };
      set('savedEdits', [...list('savedEdits'), saved]);
      return saved;
    },

    /**
     * Build My Look (spec chapter 28): per anchor item ("handle:COLOR", with
     * ":owned" for a wardrobe item), each direction already loaded and every
     * item shown around it, so a direction is never sent twice. The last few
     * anchors are kept.
     */
    builtLook(key) {
      return clone((state.builtLooks || {})[key]) || null;
    },

    setBuiltLook(key, value) {
      const looks = { ...(state.builtLooks || {}) };
      delete looks[key];
      looks[key] = clone(value);
      const keys = Object.keys(looks);
      keys.slice(0, Math.max(0, keys.length - BUILT_LOOKS_KEPT)).forEach((old) => delete looks[old]);
      set('builtLooks', looks);
    },

    /**
     * The gift note screen in the cart (update 3): the last conversation,
     * { signedIn, messages }, the shopper's messages and AMOR's answers as the
     * server returned them. Only one is kept. "New conversation", signing in
     * or out and ?reset=1 clear it.
     */
    greetingChat() {
      return clone(state.greetingChat) || null;
    },

    setGreetingChat(chat) {
      set('greetingChat', chat ? clone(chat) : null);
    },

    /**
     * "סמוך עלי" (stage א12): a conversation, { signedIn, messages }, for each
     * place it is opened from: "home" (the banner), "product:<handle>" and
     * "topic:<name>" (a content page). Kept like the gift note's, so closing
     * and opening goes on; the last six places are kept. "New conversation"
     * clears one; signing in or out and ?reset=1 clear all, but a sign-in
     * started from a conversation puts it back (trust-chat.js).
     */
    trustChat(key) {
      return clone((state.trustChats || {})[key]) || null;
    },

    setTrustChat(key, chat) {
      const chats = { ...(state.trustChats || {}) };
      delete chats[key];
      if (chat) chats[key] = clone(chat);
      const keys = Object.keys(chats);
      keys.slice(0, Math.max(0, keys.length - TRUST_CHATS_KEPT)).forEach((old) => delete chats[old]);
      set('trustChats', chats);
    },

    /** A mock order number for a payment in the conversation, after Noa's last real one (AM-10523). */
    nextOrderNumber() {
      return `AM-${10600 + Number(nextId('AM').split('-')[1])}`;
    },

    /**
     * A small copy of a request's image, shown beside it (an answer's or an
     * Edit's id), kept in this tab's session storage only: the image itself
     * is never kept (spec chapter 27).
     */
    setThumb(id, dataUrl) {
      try {
        window.sessionStorage.setItem(`${KEY}-thumb-${id}`, dataUrl);
      } catch {
        /* too large or blocked: the request shows without it */
      }
    },

    thumb(id) {
      try {
        return window.sessionStorage.getItem(`${KEY}-thumb-${id}`);
      } catch {
        return null;
      }
    },

    /* --- Dates shared by the account, fitting and returns ---------------------- */

    dateFromDays,
    isoDate,
    parseDate,
    daysUntil(iso) {
      return Math.round((parseDate(iso) - dateFromDays(0)) / DAY_MS);
    },
  };
})();
